import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Select } from '@/components/ui/primitives';
import { StockTakeSheet } from './StockTakeSheet';
import { Pager, StockStatusBadge, StoreSelect, signedMoney, useStoreOptions } from './stock-control-common';
import { PAGE_SIZE, type StockTakeSummary } from './stock-control-types';

const SHEET_STATUSES = ['open', 'closed', 'cancelled'] as const;

export function StockTakePanel({
  storeId,
  onStoreChange,
  onDirtyChange,
}: {
  storeId: string;
  onStoreChange: (storeId: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [params] = useSearchParams();
  const stores = useStoreOptions();
  const [selectedId, setSelectedId] = useState<string | null>(() => params.get('sheet'));
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);

  const sheets = useQuery({
    queryKey: ['stock-control-takes', storeId, status, offset],
    queryFn: () =>
      get<StockTakeSummary[]>(
        `/pharmacy/stock-takes${qs({ storeId: storeId || undefined, status: status || undefined, limit: PAGE_SIZE, offset })}`,
      ),
    placeholderData: keepPreviousData,
    enabled: selectedId === null,
  });

  if (selectedId) {
    return <StockTakeSheet takeId={selectedId} onBack={() => setSelectedId(null)} onDirtyChange={onDirtyChange} />;
  }

  const rowCount = sheets.data?.length ?? 0;
  const paged = offset > 0 || rowCount >= PAGE_SIZE;

  return (
    <>
      <SearchFilterBar
        filters={
          <>
            <FilterField label={tr('pharmacy', 'store')}>
              <StoreSelect
                className="w-56"
                aria-label={tr('pharmacy', 'filter_by_store')}
                value={storeId}
                onChange={(id) => {
                  onStoreChange(id);
                  setOffset(0);
                }}
                stores={stores.data}
                emptyLabel={tr('pharmacy', 'all_stores')}
              />
            </FilterField>
            <FilterField label={tr('pharmacy', 'status')}>
              <Select
                className="w-40"
                aria-label={tr('pharmacy', 'filter_by_status')}
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setOffset(0);
                }}
              >
                <option value="">{tr('pharmacy', 'all_sheets')}</option>
                {SHEET_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {humanize(option)}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        onReset={
          storeId || status
            ? () => {
                onStoreChange('');
                setStatus('');
                setOffset(0);
              }
            : undefined
        }
      />

      <DataTable
        rows={sheets.data}
        loading={sheets.isLoading} refreshing={sheets.isFetching}
        error={sheets.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setSelectedId(row.id)}
        empty={
          storeId || status
            ? tr('pharmacy', 'no_count_sheets_match_those_filters')
            : tr('pharmacy', 'no_count_sheets_yet_start_one_to_count_a_store')
        }
        footer={
          paged ? (
            <div className="w-full">
              <Pager offset={offset} pageSize={PAGE_SIZE} rowCount={rowCount} disabled={sheets.isFetching} onOffsetChange={setOffset} />
            </div>
          ) : undefined
        }
        columns={[
          { header: tr('pharmacy', 'number'), sort: (row) => row.number, cell: (row) => <span className="font-mono text-xs">{row.number}</span> },
          { header: tr('pharmacy', 'store'), sort: (row) => row.store.name, cell: (row) => row.store.name },
          { header: tr('pharmacy', 'status'), sort: (row) => row.status, cell: (row) => <StockStatusBadge status={row.status} /> },
          {
            header: tr('pharmacy', 'mode'),
            sort: (row) => row.blind,
            cell: (row) => (row.blind ? <Badge variant="secondary">{tr('pharmacy', 'blind')}</Badge> : <span className="text-muted-foreground">{tr('pharmacy', 'open')}</span>),
          },
          { header: tr('pharmacy', 'snapshot'), sort: (row) => row.snapshotAt, cell: (row) => dateTime(row.snapshotAt) },
          { header: tr('pharmacy', 'lines'), align: 'right', sort: (row) => row.lineCount, cell: (row) => row.lineCount },
          {
            header: tr('pharmacy', 'variance'),
            align: 'right',
            sort: (row) => row.varianceValueMinor,
            cell: (row) =>
              row.varianceValueMinor === null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <span className={row.varianceValueMinor < 0 ? 'font-medium text-destructive' : ''}>
                  {signedMoney(row.varianceValueMinor)}
                </span>
              ),
          },
          { header: tr('pharmacy', 'closed'), sort: (row) => row.closedAt, cell: (row) => (row.closedAt ? dateTime(row.closedAt) : '—') },
        ]}
      />
    </>
  );
}
